import { useCallback, useEffect, useRef } from 'react'

import { meetingsApi } from '@/features/meetings/services/meetings.api'
import { breakoutStore, type BreakoutAssignment } from '@vc/viewmodels/breakout/useBreakoutRooms'
import { useMeetingSessionStore, type RoomCredentials } from '../store/meeting-session.store'
import { withRoomToken } from '../services/room-token'
import { vclog } from './vc-log'
import { useBreakoutBroadcastStore } from '../store/breakout-broadcast.store'

/**
 * Breakout rooms, joined up.
 *
 * Two halves. The room's own state — which rooms exist, who belongs where —
 * travels as channel events, because it changes constantly and everybody has to
 * see the same thing at the same time. Actually MOVING into a room is an API
 * call, because a breakout is a room in its own right and entering one means
 * holding credentials for it. Neither half works as the other: an event cannot
 * mint a token, and a poll cannot keep an assignment panel live.
 *
 * The move itself is just a credential swap. Everything downstream — the
 * Phoenix channel, the SFU room, the producers, the consumers — is derived from
 * `roomCredentials`, and the channel effect is keyed on it, so replacing it
 * tears the old room down and builds the new one. That is why there is no
 * bespoke "switch rooms" plumbing here.
 */

type Args = {
  /** Null until the channel is joined. */
  subscribe: ((event: string, handler: (payload: unknown) => void) => () => void) | null
  sendEvent: ((event: string, payload: Record<string, unknown>) => void) | null
  /** The same channel, waiting for the server's answer. See usePhoenixChannel. */
  request?:
    | ((
        event: string,
        payload: Record<string, unknown>,
      ) => Promise<{ status: string; response: Record<string, unknown> }>)
    | null
  ready: boolean
  /** This client's peer id, so its own assignment can be picked out. */
  selfId: string | null
  /** Resolves a peer id to a display name from the roster. */
  nameOf: (id: string) => string
  /** The meeting, for minting breakout credentials. */
  meetingId: string | null
  /** A moderator's message reaching this room, however it got here. */
  onBroadcast?: (message: string, from: string) => void
  /** A room has no seat left for somebody. */
  onRoomFull?: (roomName: string) => void
  /** The room refused a command, in words a moderator can act on. */
  onCommandFailed?: (message: string) => void
  /** The moderator closed the breakout rooms (not the meeting ending). */
  onClosed?: () => void
}

/**
 * What the server's refusal means, for somebody who pressed a button.
 *
 * The wire reasons are one word each and are meant for this table, not for a
 * person: "moderator_only" on a screen is worse than nothing.
 */
const REFUSAL_TEXT: Record<string, string> = {
  moderator_only: 'Only the host or a co-host can do that.',
  not_open: 'The breakout rooms are no longer open.',
  no_such_room: 'That room no longer exists.',
  room_full: 'That room is full.',
  unavailable: 'The meeting could not be reached. Try again.',
  bad_request: 'That request was not understood.',
  timeout: 'The meeting did not answer. Try again.',
  dropped: 'You are not connected to the meeting.',
}

/** The room accepted the move, but this client could not get in. */
const JOIN_FAILED_TEXT = 'Could not join the room. Try again.'

/**
 * Credentials for another room, asked for with this room's token.
 *
 * Never the token in `roomCredentials` as it stands. That is the one this
 * client joined with, signed for an hour, and renewal keeps the fresh one
 * beside it (services/room-token) so the socket keyed on the credentials never
 * changes. Sending the original meant that an hour into the meeting every
 * Join Room was refused 401 and only reopened the prompt it had just closed —
 * the host, usually in the room longest, met it first, on every click.
 */
function withCurrentRoomToken<T>(
  creds: RoomCredentials,
  call: (roomToken: string) => Promise<T>,
): Promise<T> {
  return withRoomToken(creds.channelTopic.replace(/^room:/, ''), creds.roomToken, call)
}

export function useBreakoutSync({
  subscribe,
  sendEvent,
  request,
  ready,
  selfId,
  nameOf,
  meetingId,
  onBroadcast,
  onRoomFull,
  onCommandFailed,
  onClosed,
}: Args) {
  const setRoomCredentials = useMeetingSessionStore((s) => s.setRoomCredentials)

  // Held in a ref so a new callback identity does not tear down and rebuild
  // every breakout subscription on each render.
  const onBroadcastRef = useRef(onBroadcast)
  onBroadcastRef.current = onBroadcast

  const onRoomFullRef = useRef(onRoomFull)
  onRoomFullRef.current = onRoomFull

  const onCommandFailedRef = useRef(onCommandFailed)
  onCommandFailedRef.current = onCommandFailed

  const onClosedRef = useRef(onClosed)
  onClosedRef.current = onClosed

  /**
   * Is there a seat left in this room?
   *
   * Answered from the room list this client already holds, which every client
   * has and which the server keeps current. Somebody already in the room
   * counts as fitting — re-entering the room you are in is not an arrival.
   */
  const roomHasSpace = (roomId: string, who: string | null) => {
    const { activeBreakoutRooms, roomCapacity } = breakoutStore.getState()
    if (!roomCapacity || roomCapacity <= 0) return true
    const room = activeBreakoutRooms.find((r) => r.id === roomId)
    if (!room) return true
    if (who && room.participants.some((p) => p.id === who)) return true
    return room.participants.length < roomCapacity
  }

  const roomNameOf = (roomId: string | null) =>
    breakoutStore.getState().activeBreakoutRooms.find((r) => r.id === roomId)?.name ?? 'that room'

  /** Set on the way out of a room; sent once the main channel is live. */
  const pendingLeaveRef = useRef(false)

  /**
   * The meeting itself is over.
   *
   * Ending a meeting now closes its breakout rooms too, so both announcements
   * arrive — `meeting:ended` first, then `breakout:closed`. Answering the
   * second one normally would send this client back to a main room it is in
   * the middle of leaving: a fresh socket, a fresh SFU join, and a rebuilt
   * media stack, all for a meeting that no longer exists.
   */
  const meetingEndedRef = useRef(false)

  /**
   * The main room's credentials, kept so there is something to come back to.
   *
   * Captured on the way OUT rather than read on the way back: by then the store
   * holds the breakout's credentials, and the main room's are gone.
   */
  const mainCredentialsRef = useRef<RoomCredentials | null>(null)
  const nameOfRef = useRef(nameOf)
  nameOfRef.current = nameOf

  // ── The room's picture of itself ───────────────────────────────────────
  useEffect(() => {
    if (!ready || !subscribe) return

    const apply = (payload: unknown) => {
      // Read BEFORE applying: telling a removal apart from "not yet listed"
      // needs both sides of the change, and only one of those survives.
      const assignedBefore = breakoutStore.getState().myAssignedRoomId

      const p = (payload ?? {}) as {
        open?: boolean
        assignment?: BreakoutAssignment
        rooms?: Array<{ id: string; name: string; participantIds: string[] }>
        roster?: Array<{ id: string; name: string }>
        capacity?: number | null
      }

      // "No rooms open" now means it, wherever it was asked.
      //
      // This used to be ignored while inside a breakout, because a breakout
      // channel answered for itself and always said no — believing it wiped the
      // room list, which room we were in, and the moderator's controls with it.
      // The server resolves the meeting's room from any topic now, so the
      // answer is about the meeting. Still ignoring it left somebody who missed
      // the closing announcement sitting in a room that no longer existed, with
      // nothing able to tell them otherwise.
      if (p.open === false) {
        void returnToMain()
        breakoutStore.getState().clearBreakout()
        return
      }

      breakoutStore.getState().applyBreakoutState({
        // Settled above: anything still here says the rooms are open.
        open: true,
        assignment: p.assignment,
        rooms: p.rooms ?? [],
        roster: p.roster,
        capacity: p.capacity,
        nameOf: (id) => nameOfRef.current(id),
        selfId,
      })

      // Taken out of a room they were in: go back to the main room rather than
      // ask. Being un-assigned is not an invitation to consider — it is the
      // same thing Leave Room does, decided by somebody else.
      //
      // It has to be a REMOVAL, not merely an absence. Testing only for "in a
      // room but not assigned to one" fired on any state that arrived before
      // the join had been recorded — and every arrival re-announces the rooms,
      // so a self walk-in joiner was yanked straight back out, prompted again,
      // joined again, and went round for as long as anyone kept arriving.
      const after = breakoutStore.getState()
      const removed = !!assignedBefore && !after.myAssignedRoomId
      if (removed && after.currentBreakoutRoomId && !after.switching) {
        void returnToMain()
      }
    }

    const offs = [
      subscribe('breakout:opened', apply),
      // Carries the whole picture alongside the one person who moved, so a
      // panel showing every room stays right without asking for it again.
      subscribe('breakout:assigned', (payload) => {
        const p = (payload ?? {}) as { breakout?: unknown }
        if (p.breakout) apply(p.breakout)
      }),
      // A moderator's word to every room. Delivered here rather than in the
      // component so it arrives whichever room this client is standing in.
      subscribe('breakout:message', (payload) => {
        const p = (payload ?? {}) as {
          message?: string
          from?: string
          from_id?: string
          id?: string
          room_ids?: unknown
          room_names?: unknown
        }
        if (!p.message) return
        // Not to the person who sent it. A broadcast reaches the room the host
        // is standing in as well as the others, so announcing it back to them
        // popped a toast telling them what they had just typed — and, being a
        // notification, it read as somebody else's message at a glance.
        //
        // By id rather than by name: host and co-host can share a display name,
        // and then one host's announcement would suppress the other's toast.
        const fromMe = !!selfId && p.from_id === selfId
        if (!fromMe) onBroadcastRef.current?.(p.message, p.from ?? '')
        // Kept so the announcement can be re-read after its toast has gone.
        // Deduplicated on the server's id: a host standing in a room they
        // addressed receives their own message back, and has already shown it.
        const strings = (v: unknown): string[] =>
          Array.isArray(v) ? (v as unknown[]).filter((x): x is string => typeof x === 'string') : []
        useBreakoutBroadcastStore.getState().receive({
          id: p.id || `bb-in-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          text: p.message,
          senderName: p.from ?? '',
          senderId: p.from_id ?? null,
          sentAt: Date.now(),
          roomIds: strings(p.room_ids),
          // Resolved by the server — the ids alone mean nothing to a recipient.
          roomNames: strings(p.room_names),
        })
      }),
      subscribe('breakout:closed', () => {
        if (meetingEndedRef.current) {
          vclog('presence', 'breakout rooms closed with the meeting', {})
          breakoutStore.getState().clearBreakout()
          return
        }
        vclog('presence', 'breakout rooms closed — returning to the main room', {})
        // Whoever is inside a room has to be taken out of it, not merely told.
        void returnToMain()
        breakoutStore.getState().clearBreakout()
        onClosedRef.current?.()
      }),
      // The meeting ending ends the rooms with it. Heard here as well as by
      // the moderation hook, because this is what owns the breakout state and
      // the rooms have to stop existing for whoever is sitting in one.
      subscribe('meeting:ended', () => {
        meetingEndedRef.current = true
        breakoutStore.getState().clearBreakout()
      }),
    ]

    // Ask on every (re)subscribe, from whichever room this client is in.
    //
    // The server resolves the meeting's room from any topic in it, so a
    // breakout answers for the meeting rather than for itself — which is what
    // this used to be guarded against. Keeping the guard meant a moderator who
    // switched rooms never re-asked, and came back on a new channel still
    // holding the picture from the old one.
    sendEvent?.('breakout:get', {})

    // Back in the main room — now the "I have left" can be delivered to the
    // room that actually records it.
    if (!breakoutStore.getState().currentBreakoutRoomId && pendingLeaveRef.current) {
      pendingLeaveRef.current = false
      sendEvent?.('breakout:join', { roomId: null })
    }

    return () => offs.forEach((off) => off())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, subscribe, sendEvent, selfId])

  /** Put this client back in the main room, if it is not already there. */
  const returnToMain = useCallback(async () => {
    const store = breakoutStore.getState()
    if (!store.currentBreakoutRoomId && !mainCredentialsRef.current) return

    let main = mainCredentialsRef.current

    // Remembering is not a guarantee — a client that reloaded inside a room has
    // nothing kept, and without this it would sit in a closed room forever
    // while everyone else was recalled. Asking always works.
    if (!main && meetingId) {
      const creds = useMeetingSessionStore.getState().roomCredentials
      if (creds?.roomToken) {
        try {
          const res = await withCurrentRoomToken(creds, (roomToken) =>
            meetingsApi.mainRoomCredentials(meetingId, roomToken),
          )
          main = res.roomCredentials
        } catch (err) {
          vclog('presence', 'could not get main room credentials', {
            error: err instanceof Error ? err.message : String(err),
          })
        }
      }
    }

    if (!main) return
    mainCredentialsRef.current = null
    breakoutStore.getState().setCurrentBreakoutRoomId(null)
    setRoomCredentials(main)
  }, [setRoomCredentials, meetingId])

  // ── Moving ─────────────────────────────────────────────────────────────

  /**
   * Send, and answer: did the room do it?
   *
   * Two failures used to be invisible here and they look identical from a
   * chair. With no channel, `sendEvent?.(...)` evaporated — the button worked,
   * the room never heard, nothing said so. And with a channel, a command the
   * server REFUSED was answered on a `phx_reply` nothing was reading, so a
   * moderator whose role had gone stale pressed End Breakout Rooms and watched
   * the meeting carry on. Both are loud now.
   */
  const sendChecked = useCallback(
    async (event: string, payload: Record<string, unknown>): Promise<boolean> => {
      if (!sendEvent) {
        vclog('presence', 'breakout command dropped — no channel', { event })
        onCommandFailedRef.current?.(REFUSAL_TEXT.dropped)
        return false
      }

      // Older callers with no reply channel keep the fire-and-forget path
      // rather than pretending to have been answered.
      if (!request) {
        sendEvent(event, payload)
        return true
      }

      const reply = await request(event, payload)
      if (reply.status === 'ok') return true

      const reason =
        typeof reply.response?.reason === 'string' ? reply.response.reason : reply.status
      vclog('presence', 'breakout command refused', { event, reason })
      onCommandFailedRef.current?.(
        REFUSAL_TEXT[reason] ?? 'That did not work. Try again.',
      )
      return false
    },
    [sendEvent, request],
  )

  /** Fire it, and say so if the room refuses — without making the caller wait. */
  const send = useCallback(
    (event: string, payload: Record<string, unknown>) => {
      if (!sendEvent) {
        vclog('presence', 'breakout command dropped — no channel', { event })
        onCommandFailedRef.current?.(REFUSAL_TEXT.dropped)
        return false
      }
      void sendChecked(event, payload)
      return true
    },
    [sendEvent, sendChecked],
  )

  const joinRoom = useCallback(
    async (roomId: string) => {
      if (!meetingId) return
      const store = breakoutStore.getState()

      // Already there, or already on the way. Re-entering costs a full teardown
      // and rebuild of the media stack, which is what the leave/join/leave
      // flicker looked like from the outside.
      if (store.currentBreakoutRoomId === roomId || store.switching) {
        store.setInvitePromptOpen(false)
        return
      }

      // A full room is a no before anything moves. Checked here rather than
      // announced and then undone, so nothing is torn down for a move that was
      // never going to hold.
      if (!roomHasSpace(roomId, selfId)) {
        vclog('presence', 'breakout room is full', { roomId })
        onRoomFullRef.current?.(roomNameOf(roomId))
        store.setInvitePromptOpen(true)
        return
      }

      store.setSwitching(true)
      // Answered. Closing it here rather than after the credentials arrive
      // means the prompt is gone the moment the room is chosen, instead of
      // lingering across a network round trip.
      store.setInvitePromptOpen(false)
      try {
        // Tell the room BEFORE moving: this client is about to leave the main
        // channel, and afterwards it cannot send on it at all.
        //
        // And wait for its answer. The room is the authority on who fits and
        // on which rooms exist — the check above is this client's copy of both
        // — so moving on regardless meant a refused join still tore down the
        // media stack and rebuilt it somewhere the room had said no to.
        const accepted = await sendChecked('breakout:join', { roomId })
        if (!accepted) {
          store.setInvitePromptOpen(true)
          return
        }

        // Whatever room this client is in right now is the credential for
        // getting into the next one — including a breakout, when somebody is
        // moving straight from one room to another.
        const creds = useMeetingSessionStore.getState().roomCredentials
        if (!creds?.roomToken) {
          vclog('presence', 'cannot join breakout — no room token', { roomId })
          onCommandFailedRef.current?.(JOIN_FAILED_TEXT)
          store.setInvitePromptOpen(true)
          return
        }

        const { roomCredentials } = await withCurrentRoomToken(creds, (roomToken) =>
          meetingsApi.breakoutCredentials(meetingId, roomId, roomToken),
        )

        // Remember where home is before the store forgets.
        if (!mainCredentialsRef.current) {
          mainCredentialsRef.current = useMeetingSessionStore.getState().roomCredentials
        }
        store.setCurrentBreakoutRoomId(roomId)
        store.setInvitePromptOpen(false)
        setRoomCredentials(roomCredentials)
        vclog('presence', 'joined breakout room', { roomId })
      } catch (err) {
        vclog('presence', 'could not join breakout room', {
          roomId,
          error: err instanceof Error ? err.message : String(err),
        })
        // The move failed, so this client is still in the main room and the
        // prompt is still the way in. Said as well: the prompt coming back on
        // its own read as the same invitation again, not as a join that failed.
        onCommandFailedRef.current?.(JOIN_FAILED_TEXT)
        store.setInvitePromptOpen(true)
      } finally {
        breakoutStore.getState().setSwitching(false)
      }
    },
    [meetingId, sendChecked, setRoomCredentials],
  )

  /** Leave the breakout and go back to the main room, which stays open. */
  const leaveRoom = useCallback(async () => {
    // Announced once the main channel exists, not on a timer.
    //
    // `sendEvent` here is the BREAKOUT channel's — captured when this callback
    // was made — and a `setTimeout(0)` fired long before React had re-rendered
    // with the main room's channel. So the message went to the breakout, which
    // keeps no assignments, and the main room never learned the person had
    // left: they stayed listed in their old room in everyone's panel. The flag
    // is picked up by the subscribe effect below, which runs when the channel
    // changes — by which time it is the right one.
    pendingLeaveRef.current = true
    await returnToMain()
  }, [returnToMain])

  // ── Moderator actions ──────────────────────────────────────────────────

  const openRooms = useCallback(
    (
      rooms: Array<{ id: string; name: string; participantIds: string[] }>,
      assignment: BreakoutAssignment,
      /** How many fit in one room. The server is what enforces it. */
      capacity?: number | null,
    ) => {
      send('breakout:open', { rooms, assignment, capacity: capacity ?? null })
    },
    [send],
  )

  /**
   * Move somebody else. Refused here if the room is full — the moderator is
   * told rather than the move quietly not happening.
   */
  const moveParticipant = useCallback(
    (userId: string, roomId: string | null) => {
      if (roomId && !roomHasSpace(roomId, userId)) {
        vclog('presence', 'breakout room is full', { userId, roomId })
        onRoomFullRef.current?.(roomNameOf(roomId))
        return false
      }

      send('breakout:join', { userId, roomId })
      return true
    },
    [send],
  )

  const closeRooms = useCallback(async () => {
    // Waited on, not assumed.
    //
    // This used to send and immediately tear its own state down, which made a
    // refusal invisible in the worst possible way: the moderator's screen left
    // the rooms, everybody else stayed in them, and the two disagreed with
    // nothing to say which was right. A refused close is now a message, and
    // the screen stays where the meeting actually is.
    const ended = await sendChecked('breakout:close', {})
    if (!ended) return

    // Cleared here as well as on the event coming back. A moderator ending the
    // rooms from inside one changes channel on the way out, so their own
    // `breakout:closed` can arrive on a topic they have already left — and then
    // the rooms control stays on the bar with nothing behind it.
    void returnToMain()
    breakoutStore.getState().clearBreakout()
  }, [sendChecked, returnToMain])

  // The broadcast screens live in the control bar, which has no idea who the
  // local peer is. Published here, where it is already known.
  useEffect(() => {
    useBreakoutBroadcastStore.getState().setMyPeerId(selfId)
  }, [selfId])

  // Leaving the meeting screen takes the rooms with it.
  //
  // The store is module-scoped and survives the meeting, so without this the
  // NEXT meeting opened in the same tab started out believing breakout rooms
  // were running: a control bar full of rooms nobody could be in, and an
  // assignment panel showing the last meeting's arrangement. Safe here because
  // moving between rooms swaps credentials rather than remounting this screen.
  useEffect(() => {
    return () => breakoutStore.getState().clearBreakout()
  }, [])

  /**
   * Say something to the breakout rooms. The server does the fanning out.
   *
   * `roomIds` empty (or omitted) means every room, which is what this always
   * used to be. `id` is generated by the caller and echoed back by the server
   * so the sender can recognise their own message arriving in the room they are
   * standing in, instead of showing it twice.
   */
  const broadcastMessage = useCallback(
    (message: string, roomIds: string[] = [], id?: string) => {
      const text = message.trim()
      if (!text) return
      send('breakout:broadcast', { message: text, room_ids: roomIds, ...(id ? { id } : {}) })
    },
    [send],
  )

  return {
    joinRoom,
    leaveRoom,
    openRooms,
    moveParticipant,
    closeRooms,
    returnToMain,
    broadcastMessage,
  }
}
